import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { AlertOctagon, BellRing, CalendarCheck2, CircleHelp, ClipboardList, FileText, Home, ListChecks } from "lucide-react";
import { PageHeader, Panel, Empty } from "@/components/app/PageParts";
import { IntegrationStatus } from "@/components/app/IntegrationStatus";
import { api, errorMessage } from "@/lib/api";
import { daysBetween, formatDate } from "@/lib/format";

const STATS = [
  ["properties", "Properties", Home, "properties"],
  ["documents", "Documents saved", FileText, "documents"],
  ["open_actions", "Open actions", ListChecks, "actions"],
  ["upcoming_deadlines", "Upcoming confirmed deadlines", CalendarCheck2, "deadlines"],
  ["overdue_deadlines", "Overdue", AlertOctagon, "deadlines"],
  ["unconfirmed_deadlines", "Dates to confirm", CircleHelp, "deadlines"],
  ["pending_reminders", "Saved reminders (not sent)", BellRing, "deadlines"],
  ["drafts_in_progress", "Form drafts in progress", ClipboardList, "forms"],
];

const StatCard = ({ k, label, Icon, to, value, delay }) => (
  <Link to={to} data-testid={`stat-${k}`} style={{ animationDelay: `${delay}ms` }}
    className={`group fade-up rounded-xl border bg-white p-5 shadow-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-md ${k === "overdue_deadlines" && value > 0 ? "border-red-200" : ""}`}>
    <div className="flex items-center justify-between">
      <Icon className={`h-5 w-5 ${k === "overdue_deadlines" && value > 0 ? "text-red-600" : "text-gold-dark"}`} />
      <span className="text-[11px] text-slate-400 opacity-0 transition-opacity group-hover:opacity-100">View</span>
    </div>
    <p className="mt-4 font-display text-4xl font-bold text-navy" data-testid={`stat-${k}-value`}>{value}</p>
    <p className="mt-1 text-sm text-slate-600">{label}</p>
  </Link>
);

export default function Overview() {
  const { workspace } = useOutletContext();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const base = `/app/w/${workspace.id}`;

  const load = useCallback(() => {
    api.get(`/workspaces/${workspace.id}/dashboard`).then(({ data }) => setData(data)).catch((e) => setError(errorMessage(e)));
  }, [workspace.id]);
  useEffect(load, [load]);

  if (error) return <p data-testid="overview-error" className="text-red-700">{error}</p>;
  return (
    <div data-testid="overview-page">
      <PageHeader eyebrow={workspace.is_sample ? "Sample workspace" : workspace.account_type === "agent" ? "Real estate agent" : workspace.account_type === "homeowner" ? "Homeowner" : "Workspace"} title={workspace.name}
        subtitle={data ? `Today is ${formatDate(data.today)} in ${workspace.timezone.replace(/_/g, " ")}.` : " "} />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        {STATS.map(([k, label, Icon, to], i) => (
          <StatCard key={k} k={k} label={label} Icon={Icon} to={`${base}/${to}`} value={data ? data.counts[k] : "-"} delay={i * 60} />
        ))}
      </div>
      <div className="mt-8 grid gap-6 lg:grid-cols-[1.3fr_1fr]">
        <Panel title="Next confirmed deadlines" testId="overview-next-deadlines" action={<Link to={`${base}/deadlines`} className="text-xs text-slate-500 hover:text-navy" data-testid="overview-all-deadlines-link">All deadlines</Link>}>
          {data?.next_deadlines.length ? (
            <ul className="divide-y">
              {data.next_deadlines.map((d) => {
                const diff = daysBetween(data.today, d.due_date);
                return (
                  <li key={d.id} className="flex items-center justify-between gap-4 px-5 py-3" data-testid={`overview-deadline-${d.id}`}>
                    <span className="min-w-0 truncate text-sm font-medium text-navy">{d.title}</span>
                    <span className={`shrink-0 text-xs font-mono ${diff < 0 ? "text-red-600" : "text-slate-500"}`}>
                      {formatDate(d.due_date)} · {diff < 0 ? `${-diff}d overdue` : diff === 0 ? "today" : `in ${diff}d`}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : <Empty testId="overview-no-deadlines">No confirmed deadlines yet.</Empty>}
        </Panel>
        <Panel title="Open actions" testId="overview-open-actions" action={<Link to={`${base}/actions`} className="text-xs text-slate-500 hover:text-navy" data-testid="overview-all-actions-link">All actions</Link>}>
          {data?.open_actions.length ? (
            <ul className="divide-y">
              {data.open_actions.map((a) => <li key={a.id} className="px-5 py-3 text-sm text-navy">{a.title}</li>)}
            </ul>
          ) : <Empty testId="overview-no-actions">No open actions.</Empty>}
        </Panel>
      </div>
      {data?.per_property.length > 0 && (
        <Panel title="By property" className="mt-8" testId="overview-per-property">
          <ul className="divide-y">{data.per_property.map((p) => (
            <li key={p.id} data-testid={`overview-property-${p.id}`} className="flex flex-col gap-1 px-5 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
              <span className="font-medium text-navy">{p.name}</span>
              <span className="text-xs text-slate-500">{p.documents} documents · {p.open_actions} open actions · {p.upcoming_deadlines} upcoming deadlines</span>
            </li>))}</ul>
        </Panel>
      )}
      <div className="mt-8">
        <h2 className="mb-3 font-display text-base font-semibold text-navy">Services</h2>
        <IntegrationStatus compact />
      </div>
    </div>
  );
}
